import { Component, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { DebugRoleService, DEBUG_ROLE_OPTIONS } from '../../core/auth/debug-role.service';
import { RiptSummariesMenuComponent } from '../nav/ript-summaries-menu';
import { UserMenuComponent } from '../nav/user-menu';
import { UserProfileMenuComponent } from '../nav/user-profile-menu';

/**
 * Application chrome (T-066, FR-032, NFR-04). SLDS-inspired global header + context bar
 * (no side-nav). WCAG 2.1 AA: landmark roles, keyboard-navigable, skip-to-content link.
 */
@Component({
  selector: 'app-shell',
  standalone: true,
  imports: [
    RouterOutlet,
    MatButtonModule,
    MatIconModule,
    RiptSummariesMenuComponent,
    UserMenuComponent,
    UserProfileMenuComponent,
  ],
  template: `
    <a class="slds-skip-link" href="#slds-main">Skip to main content</a>

    <!-- SLDS Global Header -->
    <header class="slds-global-header" role="banner">
      <div class="slds-ghe-brand">
        <mat-icon class="slds-ghe-waffle" aria-hidden="true">apps</mat-icon>
        <span class="slds-ghe-brand-name">RIPT</span>
      </div>
      <div class="slds-ghe-utilities">
        @if (debug.enabled) {
          <label class="ript-debug-role" title="Local role impersonation (dev only)">
            <mat-icon aria-hidden="true">science</mat-icon>
            <select
              aria-label="Impersonate role (dev only)"
              (change)="onDebugRoleChange($any($event.target).value)"
            >
              @for (opt of debugRoleOptions; track opt.value) {
                <option [value]="opt.value" [selected]="opt.value === debug.selection()">{{ opt.label }}</option>
              }
            </select>
          </label>
        }
        <app-user-profile-menu />
      </div>
    </header>

    <!-- SLDS Context Navigation Bar -->
    <nav class="slds-context-bar" aria-label="Application navigation">
      <ul class="slds-context-bar__secondary" role="menubar">
        <li class="slds-context-bar__item slds-is-active" role="presentation">
          <app-ript-summaries-menu />
        </li>
        <li class="slds-context-bar__item" role="presentation">
          <app-user-menu />
        </li>
      </ul>
    </nav>

    <main id="slds-main" class="slds-main-content" tabindex="-1">
      <router-outlet />
    </main>
  `,
  styles: [`
    .slds-skip-link {
      position: absolute;
      left: -9999px;
      &:focus { left: 0.5rem; top: 0.5rem; z-index: 1000; padding: 0.5rem; background: #fff; }
    }
    .slds-global-header {
      display: flex;
      align-items: center;
      background: linear-gradient(90deg, #032d60 0%, #16325c 100%);
      height: 52px;
      padding: 0 0.75rem;
      gap: 0.5rem;
      position: sticky;
      top: 0;
      z-index: 200;
      box-shadow: 0 2px 6px rgba(0,0,0,0.35);
    }
    .slds-ghe-brand {
      display: flex;
      align-items: center;
      gap: 0.35rem;
      flex-shrink: 0;
    }
    .slds-ghe-waffle { color: #fff; font-size: 1.25rem; cursor: pointer; }
    .slds-ghe-brand-name {
      color: #fff;
      font-size: 0.95rem;
      font-weight: 700;
      letter-spacing: 0.02em;
    }
    .slds-ghe-utilities {
      display: flex;
      align-items: center;
      flex-shrink: 0;
      margin-left: auto;
      button { color: #fff; }
    }
    .ript-debug-role {
      display: inline-flex;
      align-items: center;
      gap: 0.25rem;
      margin-right: 0.5rem;
      padding: 0.1rem 0.4rem;
      border: 1px solid rgba(255,255,255,0.4);
      border-radius: 4px;
      color: #fff;
      mat-icon { font-size: 1rem; width: 1rem; height: 1rem; }
      select {
        background: transparent;
        color: #fff;
        border: 0;
        font-size: 0.8rem;
        cursor: pointer;
        option { color: #000; }
      }
    }
    /* Context Bar — SLDS .slds-context-bar__* classes live in the global
       styles.scss so child components (e.g. the summaries-menu tab) can
       consume them under Angular's Emulated view encapsulation. */
    /* Main */
    .slds-main-content {
      min-height: calc(100vh - 96px);
      background: #f3f3f3;
    }
  `],
})
export class ShellComponent {
  readonly debug = inject(DebugRoleService);
  readonly debugRoleOptions = DEBUG_ROLE_OPTIONS;

  onDebugRoleChange(selection: string): void {
    this.debug.set(selection);
    // Reload so /me re-resolves and every guard/menu re-evaluates under the new role.
    window.location.reload();
  }
}
